import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI } from '../../services/api';
import LearnerNav from '../../components/nav/LearnerNav';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const TYPE_ICONS = {
  tutorial_published: '\uD83D\uDCD6',
  announcement: '\uD83D\uDCE2',
  flashcard_review_due: '🃏',
};

const TYPE_LABELS = {
  tutorial_published: 'New Lesson',
  announcement: 'Announcement',
  flashcard_review_due: 'Flashcard Review',
};

const FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'tutorial_published', label: 'Lessons' },
  { value: 'announcement', label: 'Announcements' },
  { value: 'flashcard_review_due', label: 'Flashcard Reviews' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
];

export default function NotificationsPage() {
  const { learner, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const limit = 20;

  const fetchNotifications = useCallback(() => {
    setLoading(true);
    const params = { limit, skip: page * limit };
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    notificationAPI.list(params)
      .then(({ data }) => {
        setNotifications(data.notifications || []);
        setTotal(data.total || 0);
      })
      .catch(() => { setNotifications([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [typeFilter, statusFilter, page]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = (n) => {
    if (n.read_at) return;
    notificationAPI.markRead(n.notification_id)
      .then(() => {
        setNotifications((prev) =>
          prev.map((x) =>
            x.notification_id === n.notification_id
              ? { ...x, read_at: new Date().toISOString() }
              : x
          )
        );
      })
      .catch(() => {});
  };

  const handleMarkAllRead = () => {
    notificationAPI.markAllRead()
      .then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      })
      .catch(() => {});
  };

  const handleClick = (n) => {
    handleMarkRead(n);
    if (n.type === 'tutorial_published' && n.tutorial_id) {
      navigate(`/tutorials/${n.domain || 'general'}/play/${n.tutorial_id}`);
    } else if (n.type === 'flashcard_review_due' && n.domain) {
      navigate(`/flashcards/${n.domain}`);
    } else if (n.type === 'announcement' || n.type?.startsWith('capstone_')) {
      setSelectedNotification(n);
      setShowModal(true);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const hasUnread = notifications.some((n) => !n.read_at);

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8F0' }}>
      <LearnerNav />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1E1E24' }}>Notifications</h1>
        </div>
        {hasUnread && (
          <button
            onClick={handleMarkAllRead}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid var(--accent, #4f46e5)',
              background: 'transparent',
              color: 'var(--accent, #4f46e5)',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>Type:</span>
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setTypeFilter(opt.value); setPage(0); }}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: typeFilter === opt.value ? '1px solid var(--accent, #4f46e5)' : '1px solid var(--border-color, #d1d5db)',
                background: typeFilter === opt.value ? 'var(--accent, #4f46e5)' : 'transparent',
                color: typeFilter === opt.value ? '#fff' : 'inherit',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>Status:</span>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setStatusFilter(opt.value); setPage(0); }}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: statusFilter === opt.value ? '1px solid var(--accent, #4f46e5)' : '1px solid var(--border-color, #d1d5db)',
                background: statusFilter === opt.value ? 'var(--accent, #4f46e5)' : 'transparent',
                color: statusFilter === opt.value ? '#fff' : 'inherit',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div style={{
        border: '1px solid var(--border-color, #e5e7eb)',
        borderRadius: 12,
        overflow: 'hidden',
        background: 'var(--surface-primary, #fff)',
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>{'\uD83D\uDD14'}</div>
            <div style={{ fontWeight: 500 }}>No notifications</div>
            <div style={{ fontSize: '0.85rem', marginTop: 4 }}>You're all caught up!</div>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.notification_id}
              onClick={() => handleClick(n)}
              style={{
                padding: '14px 20px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-color, #f3f4f6)',
                background: n.read_at ? 'transparent' : 'var(--surface-secondary, #f0f4ff)',
                display: 'flex',
                gap: 14,
                alignItems: 'flex-start',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-secondary, #f9fafb)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = n.read_at ? 'transparent' : 'var(--surface-secondary, #f0f4ff)'; }}
            >
              <span style={{ fontSize: '1.3rem', flexShrink: 0, marginTop: 2 }}>
                {TYPE_ICONS[n.type] || '\uD83D\uDD14'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: n.read_at ? 500 : 600, fontSize: '0.95rem' }}>
                  {n.title}
                </div>
                {n.body_md && (
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#6b7280',
                    marginTop: 4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {n.body_md}
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: n.type === 'announcement' ? '#fef3c7' : '#dbeafe',
                    color: n.type === 'announcement' ? '#92400e' : '#1e40af',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}>
                    {TYPE_LABELS[n.type] || n.type}
                  </span>
                  {n.domain && <span>{n.domain}</span>}
                  <span>{timeAgo(n.created_at)}</span>
                </div>
              </div>
              {!n.read_at && (
                <span style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#4f46e5',
                  flexShrink: 0,
                  marginTop: 8,
                }} />
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: '1px solid var(--border-color, #d1d5db)',
              background: 'transparent',
              cursor: page === 0 ? 'default' : 'pointer',
              opacity: page === 0 ? 0.4 : 1,
              fontSize: '0.85rem',
            }}
          >
            Previous
          </button>
          <span style={{ padding: '6px 8px', fontSize: '0.85rem', color: '#6b7280' }}>
            {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: '1px solid var(--border-color, #d1d5db)',
              background: 'transparent',
              cursor: page >= totalPages - 1 ? 'default' : 'pointer',
              opacity: page >= totalPages - 1 ? 0.4 : 1,
              fontSize: '0.85rem',
            }}
          >
            Next
          </button>
        </div>
      )}

      {showModal && selectedNotification && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: 24,
              maxWidth: 480, width: '90%', maxHeight: '70vh', overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>{selectedNotification.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {selectedNotification.body_md || selectedNotification.body || 'No content available.'}
            </p>
            {selectedNotification.link && (
              <a href={selectedNotification.link} style={{ color: '#667eea' }}>
                View Details →
              </a>
            )}
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
